/* eslint-disable @typescript-eslint/naming-convention */
// Type names (RequestConfig, ResponseConfig, ResponseErrorConfig) must match
// the kubb pluginClient contract — generated code imports these exact names.

import { envVariables } from "./utils/envVariables";

export type ResponseErrorConfig<TError = unknown> = Error & {
  status?: number;
  statusText?: string;
  data?: TError;
};

export type RequestConfig<TData = unknown> = {
  url?: string;
  method: "GET" | "PUT" | "PATCH" | "POST" | "DELETE";
  params?: object;
  data?: TData;
  responseType?:
    | "arraybuffer"
    | "blob"
    | "document"
    | "json"
    | "text"
    | "stream";
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
};

export type Client = typeof client;

const client = async <TData, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  const { url = "", method, data, signal, headers, params } = config;

  const queryString = params
    ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
    : "";

  const response = await fetch(
    `${envVariables.VERIFY_API_URL}${url}${queryString}`,
    {
      method,
      body: data ? JSON.stringify(data) : undefined,
      signal,
      headers: {
        "x-api-key": envVariables.COVERAGE_API_KEY,
        Accept: "application/json",
        "Content-Type": "application/json",
        ...headers,
      },
    },
  );

  const responseData = (await response.json()) as TData;

  if (!response.ok) {
    const error = new Error(
      `Verify API error: ${response.status} ${response.statusText}`,
    ) as ResponseErrorConfig<TData>;
    error.status = response.status;
    error.statusText = response.statusText;
    error.data = responseData;
    throw error;
  }

  return {
    data: responseData,
    status: response.status,
    statusText: response.statusText,
  };
};

export default client;
